// Device-tier breakpoint model — the single source of truth for which layout
// tier the viewport is in. Replaces scattered matchMedia reads and the shell's
// hardcoded max-width:1023px cutoff.
//
// Three tiers (aligned to Tailwind's default sm=640 / lg=1024):
//   phone   <640   new mobile layout
//   tablet  640-1023  today's layout (already works, untouched)
//   desktop >=1024  sidebar + full layout
//
// widthToTier is pure so the boundaries are unit-testable; useBreakpoint wraps a
// matchMedia subscription that flips tier only when a boundary is crossed.

import { useEffect, useState } from 'react';

export type DeviceTier = 'phone' | 'tablet' | 'desktop';

/** < this width is a phone. */
export const PHONE_MAX_WIDTH = 640;
/** >= this width is a desktop; between the two is a tablet. */
export const DESKTOP_MIN_WIDTH = 1024;

export function widthToTier(width: number): DeviceTier {
  if (width < PHONE_MAX_WIDTH) return 'phone';
  if (width < DESKTOP_MIN_WIDTH) return 'tablet';
  return 'desktop';
}

export interface Breakpoint {
  tier: DeviceTier;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// SSR/test-safe: default to desktop when there's no window (this is a SPA, so at
// runtime the window always exists — the guard just keeps first render sane).
function readTier(): DeviceTier {
  if (typeof window === 'undefined') return 'desktop';
  return widthToTier(window.innerWidth);
}

/** Current device tier, reactive across the phone/tablet/desktop boundaries. */
export function useBreakpoint(): Breakpoint {
  const [tier, setTier] = useState<DeviceTier>(readTier);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    // Two boundary queries cover all three tiers; recompute from innerWidth on
    // either crossing so the pure mapper stays the one place the cutoffs live.
    const queries = [
      window.matchMedia(`(max-width: ${PHONE_MAX_WIDTH - 0.02}px)`),
      window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`),
    ];
    const onChange = () => setTier(readTier());
    onChange(); // reconcile against the pre-effect state
    for (const mql of queries) {
      if (mql.addEventListener) mql.addEventListener('change', onChange);
      else mql.addListener(onChange); // legacy Safari
    }
    return () => {
      for (const mql of queries) {
        if (mql.removeEventListener) mql.removeEventListener('change', onChange);
        else mql.removeListener(onChange);
      }
    };
  }, []);

  return {
    tier,
    isPhone: tier === 'phone',
    isTablet: tier === 'tablet',
    isDesktop: tier === 'desktop',
  };
}
